import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createTemplate,
  createVersion,
  fetchEmailDetail,
  fetchEmailQueue,
  fetchEmailStats,
  fetchFinalidades,
  fetchTemplate,
  fetchTemplates,
  fetchVersions,
  previewTemplate,
  resendEmail,
  restoreVersion,
  sendTestEmail,
  updateBinding,
} from '../api/templates';

export function useTemplates(categoria?: string) {
  return useQuery({
    queryKey: ['admin', 'templates', { categoria }],
    queryFn: () => fetchTemplates(categoria),
  });
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: ['admin', 'templates', id],
    queryFn: () => fetchTemplate(id),
    enabled: !!id,
  });
}

export function useTemplateVersions(templateId: string) {
  return useQuery({
    queryKey: ['admin', 'templates', templateId, 'versoes'],
    queryFn: () => fetchVersions(templateId),
    enabled: !!templateId,
  });
}

export function useFinalidades(categoria?: string) {
  return useQuery({
    queryKey: ['admin', 'finalidades', { categoria }],
    queryFn: () => fetchFinalidades(categoria),
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
    },
  });
}

export function useCreateVersion(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conteudo: string) => createVersion(templateId, conteudo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
    },
  });
}

export function useRestoreVersion(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (versaoId: string) => restoreVersion(templateId, versaoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
    },
  });
}

export function useUpdateBinding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ nome, versaoId }: { nome: string; versaoId: string }) =>
      updateBinding(nome, versaoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'finalidades'] });
    },
  });
}

export function useTemplatePreview() {
  return useMutation({
    mutationFn: previewTemplate,
  });
}

export function useSendTestEmail() {
  return useMutation({
    mutationFn: sendTestEmail,
  });
}

const ACTIVE_EMAIL_STATUSES = new Set(['PENDING', 'PROCESSING']);

export function useEmailQueue(params: {
  status?: string;
  search?: string;
  page?: number;
  page_size?: number;
}) {
  return useQuery({
    queryKey: ['admin', 'emails', params],
    queryFn: () => fetchEmailQueue(params),
    // Enquanto houver itens não-terminais visíveis, faz polling curto para
    // refletir transições PENDING → SENT/FAILED sem o admin precisar dar F5.
    // Quando todos os itens estiverem em estado terminal, desliga o polling.
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      const hasActive = items.some((e) => ACTIVE_EMAIL_STATUSES.has(e.status));
      return hasActive ? 3_000 : false;
    },
  });
}

export function useEmailStats(refetchInterval: number | false = 15_000) {
  return useQuery({
    queryKey: ['admin', 'emails', 'stats'],
    queryFn: fetchEmailStats,
    refetchInterval,
  });
}

export function useResendEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resendEmail,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'emails'] });
    },
  });
}

export function useEmailDetail(emailId: string | null) {
  return useQuery({
    queryKey: ['admin', 'emails', 'detail', emailId],
    queryFn: () => fetchEmailDetail(emailId as string),
    enabled: !!emailId,
  });
}
